import React, { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

// Searchable single select. options: array of {value,label,search?} or string.
// `search` adds extra matchable text (ids, codes, names) without showing in the label.
export function SearchSelect({ value, onChange, options = [], placeholder = "Select...", searchPlaceholder = "Search...", testId, widthClass = "w-[260px]", disabled, triggerClass }) {
  const [open, setOpen] = useState(false);
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const sel = norm.find((o) => String(o.value) === String(value));
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} role="combobox" disabled={disabled}
          className={cn("flex h-10 w-full items-center justify-between rounded-md border border-white/[0.1] bg-[#0A0A0A] px-3 text-sm text-foreground outline-none transition-colors hover:border-white/20 focus:border-white/25 disabled:opacity-50", triggerClass)}>
          <span className={cn("truncate", !sel && "text-muted-foreground")}>{sel ? sel.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn("border-white/[0.1] bg-[#1a1a1e] p-0", widthClass)} align="start">
        <Command className="bg-transparent" filter={(val, search, keywords) => {
          // Match on label + explicit search text only — never on internal UUIDs.
          const hay = (keywords || []).join(" ").toLowerCase();
          return hay.includes(search.toLowerCase().trim()) ? 1 : 0;
        }}>
          <CommandInput placeholder={searchPlaceholder} className="text-sm" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {norm.map((o) => (
                <CommandItem key={String(o.value)} value={String(o.value)}
                  keywords={[o.label || "", o.search || ""]}
                  data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                  onSelect={() => { onChange(String(o.value)); setOpen(false); }}>
                  <span className="flex-1 truncate">{o.label}</span>
                  {String(value) === String(o.value) && <Check className="h-4 w-4 shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


// Searchable MULTI-add select: the dropdown stays open after each pick so several options can be
// added in a row. Already-selected values are check-marked and cannot be added twice. Closes only
// on outside click / Escape. `values` = currently selected, `onAdd(value)` adds one.
export function MultiSearchSelect({ values = [], onAdd, options = [], placeholder = "Select...", searchPlaceholder = "Search...", testId, widthClass = "w-[260px]", disabled, triggerClass }) {
  const [open, setOpen] = useState(false);
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const selSet = new Set(values.map(String));
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} role="combobox" disabled={disabled}
          className={cn("flex h-10 w-full items-center justify-between rounded-md border border-white/[0.1] bg-[#0A0A0A] px-3 text-sm text-foreground outline-none transition-colors hover:border-white/20 focus:border-white/25 disabled:opacity-50", triggerClass)}>
          <span className="truncate text-muted-foreground">{placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn("border-white/[0.1] bg-[#1a1a1e] p-0", widthClass)} align="start">
        <Command className="bg-transparent" filter={(val, search, keywords) => {
          const hay = (keywords || []).join(" ").toLowerCase();
          return hay.includes(search.toLowerCase().trim()) ? 1 : 0;
        }}>
          <CommandInput placeholder={searchPlaceholder} className="text-sm" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {norm.map((o) => {
                const isSel = selSet.has(String(o.value));
                return (
                  <CommandItem key={String(o.value)} value={String(o.value)}
                    keywords={[o.label || "", o.search || ""]}
                    data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                    className={isSel ? "opacity-60" : undefined}
                    onSelect={() => { if (!isSel) onAdd(String(o.value)); }}>
                    <span className="flex-1 truncate">{o.label}</span>
                    {isSel && <Check className="h-4 w-4 shrink-0 text-[#22C55E]" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
